import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useOrgStore } from '@/stores/orgStore'
import { credentialsApi } from '@/api/clients/apiClient'
import type { Credential, CredentialType, CreateCredentialRequest } from '@/api/types'

function formatDate(s?: string) {
  if (!s) return '—'
  return new Date(s).toLocaleString()
}

const CRED_TYPES: { value: CredentialType; label: string }[] = [
  { value: 'token', label: 'Token' },
  { value: 'ssh-key', label: 'SSH Key' },
  { value: 'basic', label: 'Basic (user:pass)' },
  { value: 'iam-role', label: 'IAM Role' },
]

interface DataFields { token: string; username: string; password: string; sshKey: string; roleArn: string }
const EMPTY_FIELDS: DataFields = { token: '', username: '', password: '', sshKey: '', roleArn: '' }

function buildCredentialData(type: CredentialType, f: DataFields): string {
  switch (type) {
    case 'token':    return JSON.stringify({ token: f.token })
    case 'basic':    return JSON.stringify({ username: f.username, password: f.password })
    case 'ssh-key':  return JSON.stringify({ key: f.sshKey })
    case 'iam-role': return JSON.stringify({ role_arn: f.roleArn })
  }
}

function hasDataFields(type: CredentialType, f: DataFields): boolean {
  switch (type) {
    case 'token':    return !!f.token
    case 'basic':    return !!(f.username || f.password)
    case 'ssh-key':  return !!f.sshKey
    case 'iam-role': return !!f.roleArn
  }
}

function DataFieldsInput({ type, fields, setFields, optional = false }: {
  type: CredentialType; fields: DataFields; setFields: (f: DataFields) => void; optional?: boolean
}) {
  const cls = 'w-full px-3 py-2 text-sm font-mono rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark'
  const lbl = 'block text-sm font-medium text-text-light dark:text-text-dark mb-1'
  const optTag = optional ? <span className="text-subtle-light dark:text-subtle-dark font-normal ml-1">(leave blank to keep existing)</span> : null

  if (type === 'token') return (
    <div>
      <label className={lbl}>Token{optTag}</label>
      <input type="password" value={fields.token} required={!optional}
        onChange={(e) => setFields({ ...fields, token: e.target.value })}
        placeholder={optional ? 'Leave blank to keep existing' : 'ghp_... or github_pat_...'}
        className={cls} />
    </div>
  )
  if (type === 'basic') return (
    <div className="space-y-2">
      <label className={lbl}>Credentials{optTag}</label>
      <input type="text" value={fields.username} required={!optional}
        onChange={(e) => setFields({ ...fields, username: e.target.value })}
        placeholder="Username" className={cls} />
      <input type="password" value={fields.password} required={!optional}
        onChange={(e) => setFields({ ...fields, password: e.target.value })}
        placeholder="Password" className={cls} />
    </div>
  )
  if (type === 'ssh-key') return (
    <div>
      <label className={lbl}>Private Key (PEM){optTag}</label>
      <textarea value={fields.sshKey} required={!optional} rows={6}
        onChange={(e) => setFields({ ...fields, sshKey: e.target.value })}
        placeholder={optional ? 'Leave blank to keep existing' : '-----BEGIN OPENSSH PRIVATE KEY-----\n...'}
        className={`${cls} resize-y`} />
    </div>
  )
  return (
    <div>
      <label className={lbl}>IAM Role ARN{optTag}</label>
      <input type="text" value={fields.roleArn} required={!optional}
        onChange={(e) => setFields({ ...fields, roleArn: e.target.value })}
        placeholder={optional ? 'Leave blank to keep existing' : 'arn:aws:iam::123456789012:role/MyRole'}
        className={cls} />
    </div>
  )
}

export function CreateCredentialModal({
  orgId,
  onClose,
  onCreated,
}: {
  orgId: string
  onClose: () => void
  onCreated?: (id: string) => void
}) {
  const queryClient = useQueryClient()
  const [form, setForm] = useState<CreateCredentialRequest>({
    name: '',
    credential_type: 'token',
    data: '',
    hint: '',
  })
  const [fields, setFields] = useState<DataFields>(EMPTY_FIELDS)
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: (req: CreateCredentialRequest) => credentialsApi.create(orgId, req),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['credentials', orgId] })
      onCreated?.(data.id)
      onClose()
    },
    onError: (err: Error) => setError(err.message),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-float dark:shadow-none dark:border dark:border-border-dark w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-light dark:border-border-dark">
          <h2 className="text-lg font-semibold text-text-light dark:text-text-dark">Create Credential</h2>
          <button onClick={onClose} className="text-subtle-light dark:text-subtle-dark">
            <span className="material-icons">close</span>
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            mutation.mutate({ ...form, data: buildCredentialData(form.credential_type, fields) })
          }}
          className="p-6 space-y-4"
        >
          {error && (
            <p className="text-sm text-error bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">{error}</p>
          )}
          <div>
            <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. GitHub Token"
              required
              className="w-full px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">Type</label>
            <select
              value={form.credential_type}
              onChange={(e) => {
                setForm({ ...form, credential_type: e.target.value as CredentialType })
                setFields(EMPTY_FIELDS)
              }}
              className="w-full pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
            >
              {CRED_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <DataFieldsInput type={form.credential_type} fields={fields} setFields={setFields} />
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg text-text-light dark:text-text-dark">
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 text-sm rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-50"
            >
              {mutation.isPending ? 'Creating…' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function ResourceUsageModal({ cred, onClose }: { cred: Credential; onClose: () => void }) {
  const [query, setQuery] = useState('')
  const resources = cred.resources ?? []
  const q = query.trim().toLowerCase()
  const filtered = q ? resources.filter((r) => r.name.toLowerCase().includes(q)) : resources
  const sources = filtered.filter((r) => r.type === 'source')
  const handlers = filtered.filter((r) => r.type === 'cmdb_handler')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-float dark:shadow-none dark:border dark:border-border-dark w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-light dark:border-border-dark">
          <div>
            <h2 className="text-base font-semibold text-text-light dark:text-text-dark">Used By</h2>
            <p className="text-xs text-subtle-light dark:text-subtle-dark mt-0.5">{cred.name}</p>
          </div>
          <button onClick={onClose} className="text-subtle-light dark:text-subtle-dark">
            <span className="material-icons">close</span>
          </button>
        </div>
        <div className="px-4 pt-3 pb-0">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 material-icons text-base text-subtle-light dark:text-subtle-dark">search</span>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter resources…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </div>
        <div className="p-4 space-y-4 max-h-80 overflow-y-auto">
          {filtered.length === 0 && (
            <p className="text-sm text-subtle-light dark:text-subtle-dark text-center py-4">
              {resources.length === 0 ? 'Not used by any resource.' : 'No matches.'}
            </p>
          )}
          {sources.length > 0 && (
            <div>
              <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark uppercase tracking-wide mb-2">
                Sources ({sources.length})
              </p>
              <div className="space-y-1">
                {sources.map((r) => (
                  <Link
                    key={r.id}
                    to={`/sources/${r.id}`}
                    onClick={onClose}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-background-light dark:hover:bg-background-dark transition-colors text-sm text-text-light dark:text-text-dark"
                  >
                    <span className="material-icons text-base text-subtle-light dark:text-subtle-dark">source</span>
                    <span className="flex-1 truncate">{r.name}</span>
                    <span className="material-icons text-sm text-subtle-light dark:text-subtle-dark">chevron_right</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
          {handlers.length > 0 && (
            <div>
              <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark uppercase tracking-wide mb-2">
                CMDB Handlers ({handlers.length})
              </p>
              <div className="space-y-1">
                {handlers.map((r) => (
                  <Link
                    key={r.id}
                    to={`/cmdb/handlers/${r.id}`}
                    onClick={onClose}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-background-light dark:hover:bg-background-dark transition-colors text-sm text-text-light dark:text-text-dark"
                  >
                    <span className="material-icons text-base text-subtle-light dark:text-subtle-dark">sync</span>
                    <span className="flex-1 truncate">{r.name}</span>
                    <span className="material-icons text-sm text-subtle-light dark:text-subtle-dark">chevron_right</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function EditCredentialModal({ orgId, cred, onClose }: { orgId: string; cred: Credential; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState(cred.name)
  const [credType, setCredType] = useState<CredentialType>(cred.credential_type as CredentialType)
  const [fields, setFields] = useState<DataFields>(EMPTY_FIELDS)
  const [hint, setHint] = useState(cred.hint)
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: () => credentialsApi.update(orgId, cred.id, {
      name,
      credential_type: credType,
      data: hasDataFields(credType, fields) ? buildCredentialData(credType, fields) : undefined,
      hint,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credentials', orgId] })
      onClose()
    },
    onError: (err: Error) => setError(err.message),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-surface-light dark:bg-gradient-to-r dark:from-[#0d1b2a] dark:to-[#111e2e] rounded-xl shadow-float dark:shadow-none dark:border dark:border-border-dark w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-light dark:border-border-dark">
          <h2 className="text-lg font-semibold text-text-light dark:text-text-dark">Edit Credential</h2>
          <button onClick={onClose} className="text-subtle-light dark:text-subtle-dark">
            <span className="material-icons">close</span>
          </button>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); mutation.mutate() }}
          className="p-6 space-y-4"
        >
          {error && (
            <p className="text-sm text-error bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">{error}</p>
          )}
          <div>
            <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">Type</label>
            <select
              value={credType}
              onChange={(e) => {
                setCredType(e.target.value as CredentialType)
                setFields(EMPTY_FIELDS)
              }}
              className="w-full pl-3 pr-10 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
            >
              {CRED_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <DataFieldsInput type={credType} fields={fields} setFields={setFields} optional />
          <div>
            <label className="block text-sm font-medium text-text-light dark:text-text-dark mb-1">
              Hint <span className="text-subtle-light dark:text-subtle-dark font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={hint}
              onChange={(e) => setHint(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm rounded-lg text-text-light dark:text-text-dark">
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 text-sm rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-50"
            >
              {mutation.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function Credentials() {
  const { currentOrgId: orgId } = useOrgStore()
  const queryClient = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [editingCred, setEditingCred] = useState<Credential | null>(null)
  const [viewingResourcesCred, setViewingResourcesCred] = useState<Credential | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const { data: credentials = [], isLoading } = useQuery({
    queryKey: ['credentials', orgId],
    queryFn: () => credentialsApi.list(orgId),
    enabled: !!orgId,
  })

  const deleteMutation = useMutation({
    mutationFn: (credId: string) => credentialsApi.delete(orgId, credId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credentials', orgId] })
      queryClient.invalidateQueries({ queryKey: ['sources', orgId] })
      setDeletingId(null)
    },
  })

  return (
    <div className="p-6 space-y-6">
      {showCreate && <CreateCredentialModal orgId={orgId} onClose={() => setShowCreate(false)} />}
      {editingCred && <EditCredentialModal orgId={orgId} cred={editingCred} onClose={() => setEditingCred(null)} />}
      {viewingResourcesCred && <ResourceUsageModal cred={viewingResourcesCred} onClose={() => setViewingResourcesCred(null)} />}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-light dark:text-text-dark">Credentials</h1>
          <p className="text-sm text-subtle-light dark:text-subtle-dark mt-1">
            Encrypted credentials that can be assigned to sources and CMDB handlers.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-white rounded-xl hover:opacity-90 transition-opacity"
        >
          <span className="material-icons text-base">add</span>
          Create Credential
        </button>
      </div>

      <div className="rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-container-light dark:bg-container-dark">
              <tr>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">Name</th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">Type</th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">Hint</th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">Used By</th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">Last Updated</th>
                <th className="px-6 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">Last Used</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-subtle-light dark:text-subtle-dark text-sm">
                    Loading…
                  </td>
                </tr>
              ) : credentials.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-subtle-light dark:text-subtle-dark text-sm">
                    No credentials yet. Create one to assign to sources or CMDB handlers.
                  </td>
                </tr>
              ) : (
                credentials.map((cred) => (
                  <tr
                    key={cred.id}
                    className="border-t border-border-light dark:border-border-dark hover:bg-background-light dark:hover:bg-background-dark"
                  >
                    <td className="px-6 py-3 font-medium text-text-light dark:text-text-dark">{cred.name}</td>
                    <td className="px-6 py-3 text-subtle-light dark:text-subtle-dark font-mono text-xs">
                      {cred.credential_type || '—'}
                    </td>
                    <td className="px-6 py-3 font-mono text-xs text-text-light dark:text-text-dark">
                      {cred.hint || '—'}
                    </td>
                    <td className="px-6 py-3">
                      {!cred.resources?.length ? (
                        <span className="text-xs text-subtle-light dark:text-subtle-dark">—</span>
                      ) : (
                        <button
                          onClick={() => setViewingResourcesCred(cred)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                        >
                          <span className="material-icons text-[12px]">link</span>
                          {cred.resources.length} {cred.resources.length === 1 ? 'resource' : 'resources'}
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-3 text-subtle-light dark:text-subtle-dark text-xs">
                      {formatDate(cred.updated_at)}
                    </td>
                    <td className="px-6 py-3 text-subtle-light dark:text-subtle-dark text-xs">
                      {cred.last_used_at ? formatDate(cred.last_used_at) : '—'}
                    </td>
                    <td className="px-6 py-3 text-right">
                      {deletingId === cred.id ? (
                        <div className="flex items-center justify-end gap-2">
                          <span className="text-xs text-subtle-light dark:text-subtle-dark">Delete?</span>
                          <button
                            onClick={() => deleteMutation.mutate(cred.id)}
                            disabled={deleteMutation.isPending}
                            className="text-xs text-error hover:underline"
                          >
                            Yes
                          </button>
                          <button
                            onClick={() => setDeletingId(null)}
                            className="text-xs text-subtle-light dark:text-subtle-dark hover:underline"
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setEditingCred(cred)}
                            className="p-1.5 rounded-md text-subtle-light dark:text-subtle-dark hover:text-primary hover:bg-primary/10 transition-colors"
                            title="Edit credential"
                          >
                            <span className="material-icons text-base">edit</span>
                          </button>
                          <button
                            onClick={() => setDeletingId(cred.id)}
                            className="p-1.5 rounded-md text-subtle-light dark:text-subtle-dark hover:text-error hover:bg-error/10 transition-colors"
                            title="Delete credential"
                          >
                            <span className="material-icons text-base">delete_outline</span>
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
